import { useEffect, useState } from "react"

// ** Third Party Components
import axios from "axios"
import { parseCookies } from "nookies"
import { Plus, PlusCircle, X } from "lucide-react"
import { useMutation } from "@tanstack/react-query"
import { useNavigate, useSearchParams } from "react-router-dom"

// ** Variables
import { ACCESS_TOKEN } from "@pma/utils/constants"
import {
  EDIT_PROJECT_BY_CURRENT_USER_ENDPOINT,
  ROLE_DELETE,
  ROLE_SET,
  SERVER_URL,
} from "@pma/utils/api-routes"

// ** Types
import { DictType } from "@pma/types"

// ** Stores
import { useAuth } from "@pma/stores/auth-store"

// ** Theme Components
import { Input } from "../common/input"
import { Switch } from "@components/common/switch"
import { Button } from "@components/common/button"
import { Avatar, AvatarImage } from "@components/common/avatar"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../common/select"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@components/common/dialog"

// ** Custom Hooks
import { toast } from "@hooks/use-toast"
import useTranslator from "@hooks/use-translator"

interface Props {
  project: DictType
  list: any[]
  open: boolean
  handleClose: () => void
}

const AssignToProject = ({ project, list, open, handleClose }: Props) => {
  const navigate = useNavigate()
  const { authConfig } = useAuth()
  const { translate } = useTranslator()
  const [searchParam] = useSearchParams()

  const accessToken = parseCookies()[ACCESS_TOKEN]
  const newParams = new URLSearchParams(searchParam)
  const organizationUsers = authConfig?.organizationUsers

  const [openDialog, setOpenDialog] = useState(open)
  const [selectedUser, setSelectedUser] = useState("")
  const [deletedUser, setDeletedUser] = useState<any[]>([])
  const [searchValue, setSearchValue] = useState<string>("")
  const [filterUserList, setFilterUserList] = useState<any[]>([])
  const [showSelectElement, setShowSelectElement] = useState<boolean>(false)
  const [userList, setUserList] = useState<{ user: DictType; isMember: boolean }[]>([])

  useEffect(() => {
    if (openDialog) {
      if (list?.length) setUserList(list)
    } else handleClose()
  }, [openDialog])

  const handleUnselect = (user: DictType) => {
    setDeletedUser((pre: any) => [...pre, user])
    setUserList((prev) => prev.filter((u) => u.user.id != user.id))
  }

  const manageMembersService = async () => {
    const memberIds = userList.filter((u) => u.isMember).map((u) => u.user.id)
    const response = await axios.post(
      SERVER_URL + EDIT_PROJECT_BY_CURRENT_USER_ENDPOINT,
      { project_id: project.id, members_id: memberIds },
      { headers: { Authorization: `Bearer ${accessToken}` } },
    )
    return response.data
  }

  const manageMembers = useMutation({
    mutationKey: ["add-member"],
    mutationFn: manageMembersService,
    onSuccess: () => {
      newParams.set("revalidate", "projects")
      navigate(`?${newParams.toString()}`)
      toast({
        title: translate("Success"),
        description: translate("Member added to project"),
        variant: "success",
      })
    },
    onError: () => {
      toast({
        title: translate("Error"),
        description: translate("Unable to add member"),
        variant: "error",
      })
    },
  })

  const assignRolesToUsers = async () => {
    const projectId = project.id

    // Call the API for each user in userList
    await Promise.all(
      userList.map((entry: any) => {
        if (entry?.flag == 0) {
          const userId = entry.user.id

          let payload = {
            project_id: projectId,
            user_id: userId,
          }

          if (entry?.user?._role) {
            payload = { ...payload, ...entry?.user?._role }
          } else {
            // If you have different permissions per user, change below accordingly
            const permissions = {
              deny_access: false,
              view_project: null,
              edit_project: null,
              view_tasks: null,
              edit_tasks: null,
              contractor: false,
              supervisor: false,
              designer: false,
              financial: false,
            }
            payload = { ...payload, ...permissions }
          }

          return axios.post(SERVER_URL + ROLE_SET, payload, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          })
        } else {
          return entry
        }
      }),
    )
  }

  const deleteRolesToUsers = async () => {
    // Call the API for each user in deletedUser
    await Promise.all(
      deletedUser.map((entry) => {
        if (entry?._role?.id) {
          return axios.delete(SERVER_URL + ROLE_DELETE + `/${entry?._role?.id}`, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          })
        } else return true
      }),
    )
  }

  useEffect(() => {
    if (manageMembers.isSuccess) {
      setOpenDialog(false)
    }
  }, [manageMembers])

  const handleSubmit = async () => {
    try {
      await assignRolesToUsers() // Step 1: assign roles
      await deleteRolesToUsers()
      await manageMembers.mutate() // Step 2: assign members to the project
    } catch (err) {
      toast({
        title: translate("Error"),
        description: translate("Unable to assign roles to users"),
        variant: "error",
      })
    }
  }

  const availableUsers =
    organizationUsers?.length > 0
      ? organizationUsers
          .filter((orgUser: DictType) => {
            return !userList.some((u) => u.user.id === orgUser.id)
          })
          .filter((u: DictType) => u.name)
      : []

  const handleFilterUser = () => {
    let userList: any[] = []
    const value = searchValue.toLowerCase()
    if (searchValue?.length > 0) {
      const list = availableUsers.filter((user: any) => {
        return user.name.toLowerCase().startsWith(value) || user.name.toLowerCase().includes(value)
      })
      userList = list
    } else userList = availableUsers

    setFilterUserList(userList)
  }

  useEffect(() => {
    handleFilterUser()
  }, [searchValue])

  return (
    <Dialog open={openDialog} onOpenChange={() => setOpenDialog(false)}>
      <DialogTrigger asChild>
        <Button
          variant={"ghost"}
          className='text-xs'
          size={"icon"}
          disabled={manageMembers?.isPending}
        >
          <PlusCircle className='text-primary' size={22} />
        </Button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>{translate(`Assign to project`)}</DialogTitle>
        </DialogHeader>

        <Button variant='outline' onClick={() => setShowSelectElement(true)}>
          <Plus size={16} />
          {translate("Add User")}
        </Button>
        {/* --- List of Selected + Existing Project Members --- */}

        {showSelectElement && availableUsers.length > 0 && (
          <div className=''>
            <Select
              value={selectedUser}
              onValueChange={(val) => {
                const selected = availableUsers.find((u: any) => u.id == val)
                if (selected) {
                  setUserList((prev) => [...prev, { user: selected, isMember: false, flag: 0 }])
                  setSelectedUser("") // Reset selection to hide dropdown
                  setShowSelectElement(false)
                  setSearchValue("")
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={translate("Select user to add")} />
              </SelectTrigger>
              <SelectContent className='max-h-[200px] overflow-y-auto rounded-xl'>
                <Input
                  value={searchValue}
                  onChange={(e: any) => setSearchValue(e.target.value)}
                  className='my-1'
                  autoFocus={true}
                  onKeyDown={(e) => {
                    // Prevent Radix Select from hijacking typing
                    e.stopPropagation()
                  }}
                />
                {filterUserList.map((user: any) => (
                  <SelectItem value={`${user.id}`} className='rounded-lg' key={user.id}>
                    {user.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className='mt-2 max-h-[204px] space-y-2 overflow-auto'>
          {/* Header Row */}
          <div className='sticky top-0 z-10 flex items-center justify-between bg-white px-3 pb-1 text-xs font-semibold text-muted-foreground'>
            <div className='flex-1'>{translate("Name")}</div>
            <div className='w-[80px] text-center'>{translate("Is Member")}</div>
            <div className='w-[40px] text-center'></div>
          </div>
          <hr />

          {userList.map(({ user, isMember }) => (
            <div
              key={user.id}
              className='flex items-center justify-between rounded-md border px-3 py-2'
            >
              {/* Name and Avatar */}
              <div className='flex flex-1 items-center gap-2'>
                <Avatar className='h-8 w-8'>
                  <AvatarImage
                    src={user?.user_picture?.url || `${process.env.PUBLIC_URL}/user-profile.svg`}
                    alt={user.name}
                  />
                </Avatar>
                <span className='text-sm'>{user.name}</span>
              </div>

              {/* Switch */}
              <div className='flex w-[80px] justify-center'>
                <Switch
                  checked={isMember}
                  onCheckedChange={(checked) => {
                    setUserList((prev) =>
                      prev.map((u) => (u.user.id === user.id ? { ...u, isMember: checked } : u)),
                    )
                  }}
                />
              </div>

              {/* Clear/Remove Button */}
              <div className='flex w-[40px] justify-center'>
                <X
                  size={16}
                  className='cursor-pointer'
                  color='red'
                  onClick={() => handleUnselect(user)}
                />
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button
            type="button"
            className='w-full'
            size={"sm"}
            onClick={handleSubmit}
            loading={manageMembers.isPending}
            disabled={manageMembers.isPending}
          >
            {translate("Assign Now")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default AssignToProject
